import {CircleAlertIcon} from "lucide-react";
import {PlayerDisplay} from "@/player/PlayerViewHooks.ts";
import {cn} from "@/lib/utils.ts";
import PlayerDisplaySurface from "@/player/view/PlayerDisplaySurface.tsx";
import {playerDisplayKey, usePlayerViewTransition} from "@/player/view/usePlayerViewTransition.ts";

interface PlayerViewTransitionProps {
    display: PlayerDisplay;
    loading: boolean;
    error: boolean;
}

export default function PlayerViewTransition({display, loading, error}: PlayerViewTransitionProps) {
    const transition = usePlayerViewTransition(display);
    const surfaces = transition.pending
        ? [transition.current, transition.pending]
        : [transition.current];

    return <>
        <div className="relative min-h-0 grow overflow-hidden bg-background">
            {surfaces.map(surface => {
                const key = playerDisplayKey(surface);
                const isPending = transition.pending !== undefined
                    && key === playerDisplayKey(transition.pending);
                const visible = isPending ? transition.fading : !transition.fading;

                return <div
                    key={key}
                    aria-hidden={!visible}
                    className={cn(
                        "absolute inset-0 transition-opacity duration-1000 ease-in-out motion-reduce:transition-none",
                        isPending ? "z-10" : "z-0",
                        visible ? "opacity-100" : "pointer-events-none opacity-0",
                    )}
                >
                    <PlayerDisplaySurface
                        display={surface}
                        loading={loading}
                        error={error}
                        onReady={isPending ? transition.ready : undefined}
                        onError={isPending ? transition.fail : undefined}
                    />
                </div>;
            })}
        </div>
        {transition.failed &&
            <div
                role="alert"
                className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-destructive/50 bg-popover/95 px-4 py-2 text-sm text-destructive shadow-xl backdrop-blur"
            >
                <CircleAlertIcon className="size-4"/>
                Neue Spieleransicht konnte nicht geladen werden.
            </div>}
    </>;
}
